import "./article-footer.scss";

import { SocialMediaIconProps } from "design-system/components/primitives/social-media-links/social-media-icon";
import { SocialMediaLinks } from "design-system/components/primitives/social-media-links/social-media-links";
import { SimpleTheme } from "design-system/utils/theme";
import { ReactNode } from "react";
import {
  ArticleFooterAuthorInfo,
  ArticleFooterAuthorInfoProps,
} from "./article-footer-author-info";
import {
  ArticleFooterContributorInfo,
  ArticleFooterContributorInfoProps,
} from "./article-footer-contributor-info";
import {
  ArticleFooterLinkedAuthor,
  ArticleFooterLinkedAuthorProps,
} from "./article-footer-linked-author";
import { ArticleFooterTag, ArticleFooterTagProps } from "./article-footer-tag";

export interface ArticleFooterProps {
  followUs?: Array<ArticleFooterTagProps>;
  hideAuthor?: boolean;
  authors?: Array<ArticleFooterAuthorInfoProps>;
  linkedAuthors?: Array<ArticleFooterLinkedAuthorProps>;
  hosts?: Array<ArticleFooterContributorInfoProps>;
  interviewers?: Array<ArticleFooterContributorInfoProps>;
  interviewees?: Array<ArticleFooterContributorInfoProps>;
  narrators?: Array<ArticleFooterContributorInfoProps>;
  translators?: Array<ArticleFooterContributorInfoProps>;
  editors?: Array<ArticleFooterContributorInfoProps>;
  videographers?: Array<ArticleFooterContributorInfoProps>;
  photographers?: Array<ArticleFooterContributorInfoProps>;
  photoeditors?: Array<ArticleFooterContributorInfoProps>;
  illustrators?: Array<ArticleFooterContributorInfoProps>;
  additionalCredits?: Array<ArticleFooterContributorInfoProps>;
  tagsList?: Array<ArticleFooterTagProps>;
  topicsList?: Array<ArticleFooterTagProps>;
  shareList?: Array<SocialMediaIconProps>;
  theme?: SimpleTheme;
  authorHeading?: ReactNode;
}

/**
 * This component is for displaying details below article page content.
 *
 * ## See it in use on...
 * - An [article page](/story/example-pages-article-pages-big-art-topper--story)
 *
 * - **`id: CS-001-000-00`**
 * - **`data-region: cs__article-footer`**
 */
export function ArticleFooter({
  followUs,
  hideAuthor,
  authors,
  hosts,
  interviewers,
  interviewees,
  narrators,
  translators,
  editors,
  videographers,
  photographers,
  photoeditors,
  illustrators,
  additionalCredits,
  linkedAuthors,
  tagsList,
  topicsList,
  shareList,
  theme,
  authorHeading,
}: ArticleFooterProps) {
  // hosts, interviewers, interviewees, narrators, translators, editors, videographers, photographers, photoeditors, illustrators, additionalCredits
  return (
    <div
      className="hbs-article-footer"
      data-region="cs__article-footer"
      data-theme={theme}
    >
      {followUs && followUs.length > 0 && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Follow Us:</h2>
          <div className="hbs-article-footer__row-content">
            {followUs.map((tag, i) => (
              <ArticleFooterTag key={i} {...tag} />
            ))}
          </div>
        </div>
      )}
      {authors && authors.length > 0 && !hideAuthor && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">
            {authorHeading ?? "Author"}:
          </h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--author">
            {authors.map((person, i) => (
              <ArticleFooterAuthorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!editors?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Editor:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {editors.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!hosts?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Host:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {hosts.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!illustrators?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Illustrator:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {illustrators.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!interviewees?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Interviewee:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {interviewees.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!interviewers?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Interviewer:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {interviewers.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!narrators?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Narrator:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {narrators.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!photographers?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Photographer:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {photographers.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!photoeditors?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Photo Editor:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {photoeditors.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!translators?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Translator:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {translators.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!videographers?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Videographer:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {videographers.map((person, i) => (
              <ArticleFooterContributorInfo {...person} key={i} />
            ))}
          </div>
        </div>
      )}
      {!!additionalCredits?.length && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">
            Additional Credits:
          </h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--contributor">
            {additionalCredits.map((person, i) => (
              <ArticleFooterContributorInfo
                {...person}
                isAdditionalCredits
                key={i}
              />
            ))}
          </div>
        </div>
      )}

      {linkedAuthors && linkedAuthors.length > 0 && !hideAuthor && (
        <div className="hbs-article-footer__row hbs-article-footer__row--linked-author">
          <h2 className="hbs-article-footer__row-heading">
            {authorHeading ?? "Article By"}:
          </h2>
          <div className="hbs-article-footer__row-content hbs-article-footer__row-content--linked-author">
            {linkedAuthors.map((linkedAuthor, i) => (
              <ArticleFooterLinkedAuthor
                key={i}
                name={linkedAuthor.name}
                titles={linkedAuthor.titles}
                image={linkedAuthor.image}
              />
            ))}
          </div>
        </div>
      )}

      {tagsList && tagsList.length > 0 && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Keywords:</h2>
          <div className="hbs-article-footer__row-content">
            {tagsList.map((tag, i) => (
              <ArticleFooterTag key={i} {...tag} />
            ))}
          </div>
        </div>
      )}

      {topicsList && topicsList.length > 0 && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Topics:</h2>
          <div className="hbs-article-footer__row-content">
            {topicsList.map((topic, i) => (
              <ArticleFooterTag key={i} {...topic} />
            ))}
          </div>
        </div>
      )}

      {shareList && shareList.length > 0 && (
        <div className="hbs-article-footer__row">
          <h2 className="hbs-article-footer__row-heading">Share:</h2>
          <div className="hbs-article-footer__row-content hbs-article-footer_row-content--share-list">
            <SocialMediaLinks links={shareList} />
          </div>
        </div>
      )}
    </div>
  );
}
